import { useState } from 'react';
import {
  Globe, Lock, Users, Bell, Shield, Clock,
  Eye, EyeOff, Download, Upload, RefreshCw,
  AlertTriangle, CheckCircle, XCircle, Info,
  Server, Cloud, Database, HardDrive, Cpu,
  Activity, Zap, Save, Trash2, Wifi,
  Power, Settings, Thermometer, Wind,
  Calendar, Filter, Search, Copy,
  FileText, Archive, Share, Printer
} from 'lucide-react';

export default function GlobalSettings() {
  const [activeTab, setActiveTab] = useState('system');
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('Maintenance programmée ce weekend');
  const [backupEnabled, setBackupEnabled] = useState(true);
  const [backupFrequency, setBackupFrequency] = useState('daily');
  const [retentionDays, setRetentionDays] = useState(30);
  const [showSensitive, setShowSensitive] = useState({});

  const [systemStatus, setSystemStatus] = useState({
    database: { status: 'healthy', latency: '12ms', uptime: '99.9%' },
    cache: { status: 'healthy', latency: '3ms', uptime: '100%' },
    queue: { status: 'degraded', latency: '45ms', uptime: '98.2%' },
    storage: { status: 'healthy', usage: '2.3GB / 10GB' },
    api: { status: 'healthy', requests: '1.2k/min', errors: '0.01%' },
    websocket: { status: 'healthy', connections: '42', uptime: '99.8%' }
  });

  const [backups, setBackups] = useState([
    { id: 1, date: '2026-02-18 02:00', size: '256 MB', type: 'automatique', status: 'success' },
    { id: 2, date: '2026-02-17 02:00', size: '251 MB', type: 'automatique', status: 'success' },
    { id: 3, date: '2026-02-16 02:00', size: '248 MB', type: 'automatique', status: 'success' },
    { id: 4, date: '2026-02-15 02:00', size: '245 MB', type: 'automatique', status: 'success' },
    { id: 5, date: '2026-02-14 02:00', size: '242 MB', type: 'automatique', status: 'failed' }
  ]);

  const [logs, setLogs] = useState([
    { level: 'info', message: 'Sauvegarde quotidienne terminée', time: '08:00', date: '2026-02-18' },
    { level: 'success', message: 'Mise à jour système v3.0.0 installée', time: '03:30', date: '2026-02-18' },
    { level: 'warning', message: 'Utilisation mémoire élevée (82%)', time: '14:23', date: '2026-02-17' },
    { level: 'error', message: 'Tentative de connexion échouée - IP: 192.168.1.100', time: '22:15', date: '2026-02-17' },
    { level: 'info', message: 'Nettoyage du cache effectué', time: '04:00', date: '2026-02-17' },
    { level: 'success', message: 'Base de données optimisée', time: '03:00', date: '2026-02-17' }
  ]);

  const tabs = [
    { id: 'system', label: 'Système', icon: Server },
    { id: 'maintenance', label: 'Maintenance', icon: Shield },
    { id: 'backups', label: 'Sauvegardes', icon: Database },
    { id: 'performance', label: 'Performance', icon: Activity },
    { id: 'security', label: 'Sécurité', icon: Lock },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'cron', label: 'Tâches planifiées', icon: Clock }
  ];

  const handleBackup = () => {
    alert('Sauvegarde lancée...');
  };

  const handleClearCache = () => {
    alert('Cache vidé avec succès !');
  };

  const handleOptimizeDB = () => {
    alert('Optimisation de la base de données lancée...');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'text-green-400 bg-green-500/10';
      case 'degraded': return 'text-yellow-400 bg-yellow-500/10';
      case 'failed': return 'text-red-400 bg-red-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  const getLogIcon = (level) => {
    switch(level) {
      case 'info': return <Info className="text-blue-400" size={16} />;
      case 'success': return <CheckCircle className="text-green-400" size={16} />;
      case 'warning': return <AlertTriangle className="text-yellow-400" size={16} />;
      case 'error': return <XCircle className="text-red-400" size={16} />;
      default: return <Info className="text-gray-400" size={16} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Paramètres globaux
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Configuration système et maintenance
          </p>
        </div>
        
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-gray-900 rounded-lg border border-gray-800 text-gray-300 hover:text-white hover:border-gray-700 transition flex items-center gap-2">
            <Download size={18} />
            Exporter
          </button>
          <button className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-cyan-500/20 transition flex items-center gap-2">
            <Save size={18} />
            Appliquer
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Contenu */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-950 rounded-xl border border-gray-800 p-6">
        {/* Système */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">État du système</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(systemStatus).map(([key, value]) => (
                <div key={key} className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 capitalize">{key}</p>
                    <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(value.status)}`}>
                      {value.status === 'healthy' ? 'Opérationnel' : 
                       value.status === 'degraded' ? 'Dégradé' : 'Hors ligne'}
                    </div>
                  </div>
                  {key === 'database' && (
                    <div className="space-y-1">
                      <p className="text-sm text-white">Latence: {value.latency}</p>
                      <p className="text-sm text-white">Uptime: {value.uptime}</p>
                    </div>
                  )}
                  {key === 'storage' && (
                    <div>
                      <p className="text-sm text-white">{value.usage}</p>
                      <div className="mt-2 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-500 rounded-full" style={{ width: '23%' }}></div>
                      </div>
                    </div>
                  )}
                  {key === 'api' && (
                    <div className="space-y-1">
                      <p className="text-sm text-white">{value.requests}</p>
                      <p className="text-sm text-white">Erreurs: {value.errors}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-cyan-500/5 rounded-lg border border-cyan-500/20">
              <div className="flex items-center gap-3">
                <Activity className="text-cyan-400" size={20} />
                <div>
                  <p className="text-white font-medium">Charge système</p>
                  <p className="text-sm text-gray-400">CPU: 23% • RAM: 4.2GB/16GB • Disque: 45%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Maintenance */}
        {activeTab === 'maintenance' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Mode maintenance</h2>
            
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-medium">Activer le mode maintenance</p>
                  <p className="text-sm text-gray-400">Les utilisateurs verront une page de maintenance</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={maintenance}
                    onChange={() => setMaintenance(!maintenance)}
                  />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                </label>
              </div>
              
              {maintenance && (
                <div className="mt-4">
                  <label className="block text-sm text-gray-400 mb-2">Message de maintenance</label>
                  <textarea 
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    rows="3"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    placeholder="Message à afficher aux utilisateurs..."
                  />
                  
                  <div className="mt-4 flex gap-3">
                    <button className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition">
                      Programmer une maintenance
                    </button>
                    <button className="px-4 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg hover:bg-cyan-600/30 transition">
                      Mode urgence
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sauvegardes */}
        {activeTab === 'backups' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Sauvegardes</h2>
              <button 
                onClick={handleBackup}
                className="px-4 py-2 bg-cyan-600/20 text-cyan-400 rounded-lg hover:bg-cyan-600/30 transition flex items-center gap-2"
              >
                <Download size={18} />
                Sauvegarder maintenant
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">Dernière sauvegarde</p>
                <p className="text-xl font-bold text-white">18/02/2026</p>
                <p className="text-xs text-green-400 mt-1">Réussie</p>
              </div>
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">Taille totale</p>
                <p className="text-xl font-bold text-white">1.24 GB</p>
                <p className="text-xs text-gray-400 mt-1">12 sauvegardes</p>
              </div>
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <p className="text-gray-400 text-sm">Espace restant</p>
                <p className="text-xl font-bold text-white">8.76 GB</p>
                <p className="text-xs text-green-400 mt-1">87% libre</p>
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
              <h3 className="text-white font-medium mb-3">Configuration des sauvegardes</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Sauvegarde automatique</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={backupEnabled}
                      onChange={() => setBackupEnabled(!backupEnabled)}
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Fréquence</label>
                  <select 
                    value={backupFrequency}
                    onChange={(e) => setBackupFrequency(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="hourly">Toutes les heures</option>
                    <option value="daily">Tous les jours</option>
                    <option value="weekly">Toutes les semaines</option>
                    <option value="monthly">Tous les mois</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Rétention (jours)</label>
                  <input 
                    type="number" 
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-white font-medium mb-3">Historique des sauvegardes</h3>
              <div className="space-y-2">
                {backups.map(backup => (
                  <div key={backup.id} className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      {backup.status === 'success' ? (
                        <CheckCircle className="text-green-400" size={16} />
                      ) : (
                        <XCircle className="text-red-400" size={16} />
                      )}
                      <span className="text-white">{backup.date}</span>
                      <span className="text-gray-400 text-sm">{backup.size}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      backup.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {backup.status === 'success' ? 'Succès' : 'Échec'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Logs */}
        {activeTab === 'logs' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Logs système</h2>
              <div className="flex gap-2">
                <button className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition">
                  <Filter size={18} />
                </button>
                <button className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition">
                  <Download size={18} />
                </button>
                <button className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-800/30 rounded-lg text-sm hover:bg-gray-800/50 transition">
                  {getLogIcon(log.level)}
                  <span className="text-gray-300 flex-1">{log.message}</span>
                  <span className="text-gray-500 text-xs">{log.date} {log.time}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-white mb-4">Performance système</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <h3 className="text-white font-medium mb-3">CPU</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Utilisation</span>
                    <span className="text-white">23%</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: '23%' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <h3 className="text-white font-medium mb-3">Mémoire</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">RAM</span>
                    <span className="text-white">4.2 GB / 16 GB</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '26%' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <h3 className="text-white font-medium mb-3">Disque</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Stockage</span>
                    <span className="text-white">45 GB / 100 GB</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: '45%' }}></div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                <h3 className="text-white font-medium mb-3">Réseau</h3>
                <div className="space-y-1">
                  <p className="text-sm text-gray-400">Latence moyenne: 45ms</p>
                  <p className="text-sm text-gray-400">Requêtes/min: 1.2k</p>
                  <p className="text-sm text-green-400">99.9% uptime</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button 
                onClick={handleClearCache}
                className="px-4 py-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 transition"
              >
                Vider le cache
              </button>
              <button 
                onClick={handleOptimizeDB}
                className="px-4 py-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 transition"
              >
                Optimiser la base
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}